import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import Select from '@/components/Common/Select';
import QuickActionIcon from '@/assets/icons/QuickActionIcon';
import AutomationIcon from '@/assets/icons/AutomationIcon';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { IoIosPlay } from 'react-icons/io';
import { Control } from 'react-hook-form';

export type AsteriskDrawerFormValues = {
  businessName: string;
  asteriskAccount: string;
  syncSchedule: string;
};

const STATIC_SYNC_LOGS = [
  {
    id: 1,
    date_time: 'May 8, 2026, 10:35 AM',
    status: 'completed',
    duration: '0m 7s',
    total: 170,
    new: 3,
    updated: 167,
    failed: 0
  },
  {
    id: 2,
    date_time: 'May 7, 2026, 9:12 AM',
    status: 'completed',
    duration: '0m 9s',
    total: 167,
    new: 0,
    updated: 167,
    failed: 0
  },
  {
    id: 3,
    date_time: 'May 6, 2026, 8:45 AM',
    status: 'completed',
    duration: '0m 6s',
    total: 167,
    new: 2,
    updated: 165,
    failed: 0
  }
];

interface Props {
  control: Control<AsteriskDrawerFormValues>;
}

export default function AsteriskSyncTab({ control }: Props) {
  const theme = useTheme();

  const columns: ColumnDef<(typeof STATIC_SYNC_LOGS)[0]>[] = [
    { header: 'DATE & TIME', accessorKey: 'date_time' },
    {
      header: 'STATUS',
      accessorKey: 'status',
      cell: ({ row }) => {
        const status = row.original.status;
        const colorMap: Record<string, { text: string; bg: string }> = {
          completed: { text: theme.palette.success.main, bg: theme.palette.success.light },
          failed: { text: theme.palette.error.main, bg: theme.palette.error.light },
          running: { text: theme.palette.primary.main, bg: theme.palette.primary.light }
        };
        const style = colorMap[status] ?? { text: '#374151', bg: '#F3F4F6' };
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return (
          <Chip
            label={label}
            size='small'
            sx={{ fontWeight: 500, borderRadius: '10px', color: style.text, bgcolor: style.bg }}
          />
        );
      }
    },
    { header: 'DURATION', accessorKey: 'duration' },
    { header: 'TOTAL', accessorKey: 'total' },
    {
      header: 'NEW',
      accessorKey: 'new',
      cell: ({ row }) => <Typography color='success.main'>{row.original.new}</Typography>
    },
    {
      header: 'UPDATED',
      accessorKey: 'updated',
      cell: ({ row }) => <Typography color='primary.main'>{row.original.updated}</Typography>
    },
    {
      header: 'FAILED',
      accessorKey: 'failed',
      cell: ({ row }) => (
        <Typography color='error.main'>{row.original.failed === 0 ? '—' : row.original.failed}</Typography>
      )
    }
  ];

  return (
    <Box>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        bgcolor='#fff'
        borderRadius='8px'
        gap={2}
        mb={3}
        alignItems='stretch'
      >
        <Box
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          bgcolor={theme.palette.primary.light}
          flex={1}
          padding='20px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center'>
            <QuickActionIcon />
            <Stack>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Quick Action
              </Typography>
              <Typography fontWeight={500} fontSize={16}>
                Sync Now
              </Typography>
            </Stack>
          </Stack>
          <Button startIcon={<IoIosPlay size={16} />} sx={{ minHeight: 40, flexShrink: 0 }}>
            Run Sync Now
          </Button>
        </Box>

        <Box
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          border='1px solid #ECECEC'
          flex={1}
          padding='20px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center' minWidth={0}>
            <AutomationIcon />
            <Stack minWidth={0}>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Automation
              </Typography>
              <Typography fontWeight={500} fontSize={16} noWrap>
                Sync Schedule
              </Typography>
            </Stack>
          </Stack>
          <Box>
            <Select
              label=''
              control={control}
              name='syncSchedule'
              sx={{ minWidth: 170, height: '45px' }}
              options={[
                { label: 'Every Hour', value: 'hourly' },
                { label: 'Every 6 Hour', value: 'after_six_hours' },
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' }
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Box bgcolor='#fff' padding='16px' borderRadius='8px' border='1px solid #ECECEC'>
        <Typography fontSize={16} fontWeight={500} mb={2}>
          Sync History
        </Typography>
        <DataTable data={STATIC_SYNC_LOGS} columns={columns} containerStyles={{ borderRadius: '4px' }} />
      </Box>
    </Box>
  );
}
